import React, {useEffect} from 'react';
import {Col, ConfigProvider, Flex, Layout, message, Row, theme} from 'antd';
import {Link, Outlet, useOutletContext, useParams} from "react-router-dom";
import {Typography} from 'antd';
import IconBackArrow from "../../components/ui/icons/IconBackArrow";
import PageLinks from "../../components/brief/PageLinks";
import {useTranslation} from "react-i18next";
import SwitchGreen from "../../components/ui/inputs/SwitchGreen";
import useSWRMutation from "swr/mutation";
import urls from "../../api/urls";
import {api, HydraData} from "../../api/apiProvider";
import Roles from "../../roles/Roles";
import LayoutSider from "../../components/brief/LayoutSider";
import useSWR from "swr";
import getBriefParamsString from "../../api/params";
import {Product} from "../../api/types";
import dayjs from "dayjs";

const {Title} = Typography;

const dateFormat = 'DD.MM.YYYY'

interface Brief {
  isAccepted: boolean,
  isPao: boolean,
}

type ContextType = { setBrief: Function, brief: Brief }

const {Header, Content, Sider} = Layout

export default function EditLayout() {
  const {t} = useTranslation()
  const routeParams = useParams()
  const briefId = Number(routeParams.id) || 0

  const {
    token: {borderRadiusLG},
  } = theme.useToken()

  const [brief, setBrief] = React.useState<Brief>({
    isAccepted: false,
    isPao: false,
  })

  const {
    data: briefData,
    mutate: mutateBrief,
  } = useSWR<HydraData>(briefId > 0 && briefId !== 0 ? `${urls.briefs}/${briefId}?${getBriefParamsString()}` : null, api.get, {
    revalidateOnFocus: false
  })

  useEffect(() => {
    if (briefData && briefData.data) {
      setBrief({
        isAccepted: briefData.data.isAccepted,
        isPao: briefData.data.products && briefData.data.products.some((product: Product) => product.company &&
          product.company.isBriefManyChannels),
      })
    }
  }, [briefData])

  const {trigger: updateBrief, isMutating: isBriefUpdating}
    = useSWRMutation(`${urls.briefs}/${briefId}`, api.patch, /* options */)

  const onChange = (checked: boolean) => {
    if (!Roles.hasAccess('canSetIsAcceptedInCriterion')) {
      return
    }

    updateBrief({
      isAccepted: checked,
    }).then((response) => {
      if (response && response.id) {
        message.success(t('common.saved'))
        setBrief({
          isAccepted: response.isAccepted,
          isPao: brief.isPao,
        })
      }
    }).catch(errors => {
      if (errors['hydra:description']) {
        message.error(errors['hydra:description'])
      }
    })
  }

  const getTitleText = () => {
    if (!briefData || !briefData.data) {
      return ''
    }

    const product: Product = briefData.data.products && briefData.data.products[0]

    if (product) {
      const company = product?.company
      const companyName = product?.company?.name
      const pool = company?.pool?.name
      const kpi = briefData.data.kpi ? briefData.data.kpi.name : ''

      return [pool, companyName, kpi].filter(item => item).join('/')
    }
  }

  const getTitleDescription = () => {
    if (!briefData || !briefData.data) {
      return ''
    }

    const products: Product[] = briefData.data.products

    if (products && products.length > 0) {
      const productsNames = products.map(product => {
        return product.name
      })
      const dateStart = dayjs(briefData.data.dataDateStart).format(dateFormat)
      const dateEnd = dayjs(briefData.data.dataDateEnd).format(dateFormat)

      return `${productsNames.join('/')}/${dateStart}-${dateEnd}`
    }
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Layout: {
            headerBg: '#fff',
            siderBg: '#fff',
            fontFamily: 'Open Sans',
            fontFamilyCode: 'Open Sans',
          },
        },
      }}
    >
      <Layout>
        <Header
          className={'layout-header'}>
          <Row justify={"space-between"} style={{flex: 1}} align={"middle"}>
            <Col span={4}>
              <Flex gap={'large'} align={'center'} justify={'flex-start'}>
                <Link to={{pathname: '/briefs/actual', search: `?id=${briefId}`}} style={{display: 'flex', gap: '16px'}}>
                  <Flex gap={'small'} align={'center'}>
                    <IconBackArrow/>
                  </Flex>
                  <Flex vertical className={'layout__title'} justify={'flex-start'} gap={4}>
                    <Flex className={'layout__title-text'} justify={'flex-start'}>
                      {getTitleText()}
                    </Flex>
                    <Flex className={'layout__title-description'} justify={'flex-start'}>
                      {getTitleDescription()}
                    </Flex>
                  </Flex>
                </Link>
              </Flex>
            </Col>
            <Col span={16}>
              <Title level={5} style={{margin: 0}}>
                <PageLinks brief={brief}/>
              </Title>
            </Col>
            <Col span={4}>
              <Flex gap={'small'} align={'center'} justify={'flex-end'}>
                { briefId > 0 &&
                  <Flex justify={'space-between'} align={'center'} gap={'large'}>
                    <span className={'layout__accept-text'}>{t('edit.isAccepted')}</span>
                    <Flex align={'center'} gap={4}>
                      {t('common.no')}
                      <SwitchGreen
                        size={'small'}
                        checked={brief.isAccepted}
                        onChange={onChange}
                        //disabled={!Roles.hasAccess('canSetIsAcceptedInTasks')}
                      />
                      {t('common.yes')}
                    </Flex>
                  </Flex>
                }

              </Flex>
            </Col>
          </Row>
        </Header>
        <Layout style={{paddingLeft: '48px'}}>
          <LayoutSider/>
          <Layout>
            <Content
              style={{
                minHeight: 'calc(100vh - 96px)',
                background: '#F1F5F7',
                borderRadius: borderRadiusLG,
                padding: '16px',
              }}
            >
              <Outlet context={{setBrief, brief} satisfies ContextType}/>
            </Content>
          </Layout>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
}

export function useLayoutContext() {
  return useOutletContext<ContextType>();
}
